import { DataEventListener, IDataEventsClient } from "@multiweb/clients/dataEvents";
import { ISectionLogger } from "@multiweb/logging";
import { IDataEvent, IDataEventMessage } from "@multiweb/types/contracts";

// čekání až minutu na doběhnutí připojení
const WAIT_FOR_CONNECTION_ITERATIONS = 6000;
const WAIT_FOR_CONNECTION_BACKOFF_TIME_MS = 10;
/** Určuje interval neaktivity (v ms), po kterém se pošle udržovací dotaz */
const HEARTBEAT_INTERVAL = 60000;

interface IDataEventDTO {
    MessageType: string;
    Source: string;
    Message: IDataEventMessageDTO;
}

interface IDataEventMessageDTO {
    Type: string;
    SubType: string | null;
    Time: string;
    ID: string | null;
    Body: any | null;
}

/**
 * @internal
 */
export class DataEventsClient implements IDataEventsClient {
    private readonly _communicationUrl: string;
    private readonly _heartBeatInterval: number;
    private readonly _useLegacyApi: boolean | undefined;
    private readonly _logger: ISectionLogger;
    private _heartbeatIntervalHandle: number = 0;

    private _messageEvents: { cond: RegExp; listener: DataEventListener }[] = [];
    private __webSocket!: WebSocket;

    constructor(logger: ISectionLogger, communicationUrl: string, heartBeatInterval: number = HEARTBEAT_INTERVAL, useLegacyApi?: boolean) {
        this._communicationUrl = communicationUrl;
        this._heartBeatInterval = heartBeatInterval;
        this._useLegacyApi = useLegacyApi;
        this._logger = logger;
    }

    public subscribeDataEvent(source: string, type: string, subType: string, listener: DataEventListener): void {
        this._webSocket.then((ws) => {
            const cnd = this._recDesc(source, type, subType);
            if (this._messageEvents.filter(v => v.cond.source === cnd.source).length === 0)
                this._send(ws, { target: "DataEvents", message: { command: "SubscribeEvent", eventIdentification: { source: source, type: type, subType: subType } } });
            this._messageEvents.push({ cond: this._recDesc(source, type, subType), listener });
        }).catch(e => {
            this._logger.error("Subscribe error:", e);
        });
    }

    public unsubscribeDataEvent(source: string, type: string, subType: string, listener?: DataEventListener): void {
        this._webSocket.then((ws) => {
            const cnd = this._recDesc(source, type, subType);
            if (!listener) {
                this._messageEvents = this._messageEvents.filter(v => v.cond.source !== cnd.source);
                this._send(ws, { target: "DataEvents", message: { command: "UnsubscribeEvent", eventIdentification: { source: source, type: type, subType: subType } } });
            } else {
                const old = this._messageEvents;
                this._messageEvents = [];
                let allRemoved = true;
                for (const a of old) {
                    if (a.cond.source === cnd.source) {
                        if (listener === a.listener) {
                            continue;
                        } else {
                            allRemoved = false;
                        }
                    }

                    this._messageEvents.push(a);
                }

                if (allRemoved)
                    this._send(ws, { target: "DataEvents", message: { command: "UnsubscribeEvent", eventIdentification: { source: source, type: type, subType: subType } } });
            }
        }).catch(e => {
            this._logger.error("Unsubscribe error:", e);
        });
    }

    private get _webSocket(): Promise<WebSocket> {
        return new Promise((resolve, reject) => {
            if (!this.__webSocket)
                this._createWebSocket(resolve, reject);
            else
                switch (this.__webSocket.readyState) {
                    case WebSocket.CLOSED:
                    case WebSocket.CLOSING:
                        this._createWebSocket(resolve, reject);
                        break;
                    case WebSocket.OPEN:
                        resolve(this.__webSocket);
                        break;
                    case WebSocket.CONNECTING: {
                        /**
                         * Pokud je ve stavu connecting, spustí se funkce v intervalu `WAIT_FOR_CONNECTION_BACKOFF_TIME_MS`, která čeká na dokončení připojení socketu,
                         * bohužel WS nemá událost na změnu readyState, takže se to musí dělat pomocí periodické kontroly.
                         * Je vytvořena horní hranice doby čekání, pokud se neprovede připojení ani po `WAIT_FOR_CONNECTION_FINISHED_ITERATIONS` iteracích,
                         * což odpovídá `WAIT_FOR_CONNECTION_FINISHED_ITERATIONS` * `WAIT_FOR_CONNECTION_BACKOFF_TIME_MS` ms, vyvolá se výjimka s Connection timeout.
                         */
                        let countExec = WAIT_FOR_CONNECTION_ITERATIONS;
                        const interval = self.setInterval(() => {
                            switch (this.__webSocket.readyState) {
                                case WebSocket.CLOSED:
                                case WebSocket.CLOSING:
                                    clearInterval(interval);
                                    this._createWebSocket(resolve, reject);
                                    break;
                                case WebSocket.OPEN:
                                    clearInterval(interval);
                                    resolve(this.__webSocket);
                                    break;
                            }

                            if (countExec-- < 0) {
                                clearInterval(interval);
                                reject(new Error("Connection timeout"));
                            }
                        }, WAIT_FOR_CONNECTION_BACKOFF_TIME_MS);
                        break;
                    }
                    default:
                        reject(new Error("Not Implemented"));
                }
        });
    }

    private _recDesc(source: string, type: string, subType: string): RegExp {
        return new RegExp(
            "^" +
            (source === "*" ? ".*" : source) + "\\|" +
            (type === "*" ? ".*" : type) + "\\|" +
            (subType === "*" ? ".*" : subType) +
            "$", "i");
    }

    private __wsUrl!: string;

    private get _wsUrl(): string {
        if (!this.__wsUrl) {
            if (this._communicationUrl) {
                const handlerPath = this._useLegacyApi ? "handlers/ws/websocket.aspx" : "api/v01/as/data-trans/websocket";
                const url = new URL(handlerPath, `${this._communicationUrl}/`);
                this.__wsUrl = `${url.protocol === "http:" ? "ws" : "wss"}://${url.host}${url.pathname}`;
            } else {
                throw new Error("[WebSocket] Missing communication url");
            }
        }
        return this.__wsUrl;
    }

    private _createWebSocket(resolve: (value?: WebSocket | Promise<WebSocket>) => void, reject: (reason?: any) => void): void {
        this.__webSocket = new WebSocket(this._wsUrl);
        this.__webSocket.onopen = () => {
            this._startHeartbeat();
            resolve(this.__webSocket);
        };
        this.__webSocket.onerror = (e) => reject(e);
        this.__webSocket.onclose = (e) => {
            this._logger.log("WS-CLOSED", e);
            this._stopHeartbeat();
            self.setTimeout(() => {
                this._createWebSocket(resolve, reject);
            }, 30000);
        };
        this.__webSocket.onmessage = (e) => {
            this._resetHeartbeat();

            const eventDto: IDataEventDTO = JSON.parse(e.data);
            if (eventDto.MessageType === "DataEvent") {
                const messageIdentifier = eventDto.Source + "|" + eventDto.Message.Type + "|" + eventDto.Message.SubType;
                // pokus o rozparsovani jsonu
                if (typeof eventDto.Message.Body === "string") {
                    try {
                        const json = JSON.parse(eventDto.Message.Body);
                        eventDto.Message.Body = json;
                    } catch (e) {
                        // ignored
                    }
                }

                const dataEvent = transformDataEvent(eventDto);

                for (const v of this._messageEvents)
                    if (v.cond.test(messageIdentifier)) {
                        v.listener(dataEvent);
                    }
            }
        };
    }

    /** Pošle zprávu přes předaný websocket */
    private _send(ws: WebSocket, message: object): void {
        this._resetHeartbeat();
        ws.send(JSON.stringify(message));
    }

    /** Pošle udržovací zprávu */
    private _sendHeartbeat(): void {
        this._webSocket.then(ws => {
            ws.send(JSON.stringify({ target: "DataEvents", message: { command: "HeartBeat" } }));
        }).catch(e => {
            this._logger.error("Failed to send heartbeat message:", e);
        });
    }

    /** Spustí periodické posílání udržovacích zpráv */
    private _startHeartbeat(): void {
        this._heartbeatIntervalHandle = self.setInterval(() => this._sendHeartbeat(), this._heartBeatInterval);
    }

    /** Ukončí periodické posílání udržovacích zpráv */
    private _stopHeartbeat(): void {
        self.clearInterval(this._heartbeatIntervalHandle);
    }

    /** Zresetuje interval periodického posílání udržovacích zpráv */
    private _resetHeartbeat(): void {
        this._stopHeartbeat();
        this._startHeartbeat();
    }
}

const transformDataEvent = (dataEvent: IDataEventDTO): IDataEvent => ({
    messageType: dataEvent.MessageType,
    source: dataEvent.Source,
    message: transformMessage(dataEvent.Message),
});

const transformMessage = (message: IDataEventMessageDTO): IDataEventMessage => ({
    type: message.Type,
    subType: message.SubType,
    time: message.Time,
    body: message.Body,
    id: message.ID,
});
